import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarMonthIcon,
  RequestQuote as RequestQuoteIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiUrl } from '../config/api';
import AppMonthField from '../components/inputs/AppMonthField';
import {
  BreakdownList,
  EmptyState,
  fmtMoney,
  LoadingOverlay,
  MiniStat,
  StatCard,
  TableSection,
} from '../components/reports/ReportUi';
import { formatSriLankaMonthLabel, getSriLankaMonthValue } from '../lib/sriLankaTime';

const API_URL = apiUrl('reports_dashboard');

export default function MonthlyProfitReport() {
  const theme = useTheme();
  const [reportMonth, setReportMonth] = useState(() => getSriLankaMonthValue());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedMonthLabel = useMemo(
    () => formatSriLankaMonthLabel(reportMonth),
    [reportMonth]
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ month: reportMonth });
      const response = await fetch(`${API_URL}?${params.toString()}`);
      const payload = await response.json();

      if (!response.ok || payload?.status === 'error') {
        throw new Error(payload?.message || 'Failed to load monthly profit report');
      }

      setData(payload);
    } catch (err) {
      setError(err.message || 'Failed to load monthly profit report');
    } finally {
      setLoading(false);
    }
  }, [reportMonth]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const monthProfit = data?.month_profit_summary || null;
  const salesSummary = data?.sales_summary || null;
  const salaryBreakdownSummary = data?.salary_breakdown_summary || null;
  const tipServiceSummary = data?.tip_service_summary || null;
  const organizationFundSummary = data?.organization_fund_summary || null;
  const displayedMonthLabel = data?.filters?.month_label || selectedMonthLabel;
  const timezoneLabel = data?.filters?.timezone || 'Asia/Colombo';
  const sourceTotal = Number(monthProfit?.recognized_sales || 0) + Number(monthProfit?.other_income || 0);
  const usesTotal = Number(monthProfit?.total_outgoing || 0);
  const balanceSheetRows = [
    [
      { label: 'Total Sales', value: monthProfit?.total_sales },
      { label: 'Bank Charge', value: monthProfit?.bank_charge },
    ],
    [
      { label: 'Less Credit Sales', value: monthProfit?.credit_sales },
      { label: 'Operating Expenses', value: monthProfit?.operating_expenses },
    ],
    [
      { label: 'Recognized Sales', value: monthProfit?.recognized_sales, strong: true },
      { label: 'Purchase Expenses', value: monthProfit?.purchase_expenses },
    ],
    [
      { label: 'Other Income', value: monthProfit?.other_income },
      { label: 'Net Salaries', value: monthProfit?.total_salaries },
    ],
    [
      { label: 'Source Total', value: sourceTotal, strong: true },
      { label: 'Advance Payments', value: monthProfit?.advance_payments },
    ],
    [
      { label: 'Cash Payments (split)', value: monthProfit?.cash_payments, muted: true },
      { label: 'Uses Total', value: usesTotal, strong: true },
    ],
    [
      { label: 'Card Payments (split)', value: monthProfit?.card_payments, muted: true },
      { label: 'Month Balance Profit', value: monthProfit?.balance_profit, strong: true, success: true },
    ],
    [
      { label: 'Online Payments (split)', value: monthProfit?.online_sales, muted: true },
      { label: 'Settlements already in Other Income', value: monthProfit?.other_income, muted: true },
    ],
  ];
  const headerCellSx = {
    fontWeight: 700,
    whiteSpace: 'nowrap',
    bgcolor: alpha(theme.palette.primary.main, 0.035),
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          color: '#fff',
          background: 'linear-gradient(135deg, #1b2237 0%, #294d72 55%, #2d8a63 100%)',
          boxShadow: '0 30px 60px rgba(13, 32, 52, 0.22)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 230,
            height: 230,
            borderRadius: '50%',
            top: -70,
            right: -35,
            bgcolor: 'rgba(255,255,255,0.07)',
          }}
        />

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          justifyContent="space-between"
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, opacity: 0.85 }}>
              Profit Report Per Month
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1, mb: 1.25 }}>
              Monthly profit balance for {displayedMonthLabel}
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 680, opacity: 0.9 }}>
              This page shows the month-level balance from total sales, bank charge, expenses, purchases, salaries, and advance payments using Sri Lanka time.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
            <Chip
              label={`Viewing ${displayedMonthLabel}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
            <Chip
              label={timezoneLabel}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
            <Chip
              label={`${monthProfit?.day_count || 0} active days`}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
          <AppMonthField
            label="Profit Month"
            size="small"
            value={reportMonth}
            onChange={(e) => setReportMonth(e.target.value)}
            sx={{ minWidth: 220 }}
            helperText="MM/YYYY"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Month profit reloads automatically
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Change the month and the full balance report will refresh for that month.
            </Typography>
          </Box>

          <Button
            component={RouterLink}
            to="/reports"
            variant="outlined"
            sx={{ borderRadius: 999, px: 2.2 }}
          >
            Back To Reports
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {!error ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Dates and month totals use Sri Lankan time ({timezoneLabel}).
        </Alert>
      ) : null}

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <LoadingOverlay open={loading} monthLabel={selectedMonthLabel} />

        {!data ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              display: 'grid',
              placeItems: 'center',
              minHeight: 280,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={38} thickness={4.2} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Preparing {selectedMonthLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fetching the month balance, salary totals, purchases, and bank charge.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <StatCard
                title="Total Sales"
                value={`Rs ${fmtMoney(monthProfit?.total_sales)}`}
                caption={`Gross sales including credit sales for ${displayedMonthLabel}`}
                icon={<AssessmentIcon />}
                tone="primary"
              />
              <StatCard
                title="Credit Sales"
                value={`Rs ${fmtMoney(monthProfit?.credit_sales)}`}
                caption="Deducted from total sales until settlement comes in"
                icon={<RequestQuoteIcon />}
                tone="warning"
              />
              <StatCard
                title="Recognized Sales"
                value={`Rs ${fmtMoney(monthProfit?.recognized_sales)}`}
                caption="Gross sales minus credit sales"
                icon={<AssessmentIcon />}
                tone="info"
              />
              <StatCard
                title="Month Balance"
                value={`Rs ${fmtMoney(monthProfit?.balance_profit)}`}
                caption={`Settlements/other income: Rs ${fmtMoney(monthProfit?.other_income)}`}
                icon={<TrendingUpIcon />}
                tone="success"
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: `linear-gradient(180deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between">
                <Box sx={{ maxWidth: 440 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 1.1, color: 'text.secondary' }}>
                    Profit Formula
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                    Month balance structure
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Balance = gross sales - credit sales + settlements/other income - bank charge - expenses - purchases - salaries - advance payments. Tips and service charges are analyzed separately as salary and fund allocations, not mixed into the business profit line.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', flex: 1 }}>
                  <MiniStat label="Recognized Sales" value={`Rs ${fmtMoney(monthProfit?.recognized_sales)}`} tone="success" />
                  <MiniStat label="Credit Sales Deducted" value={`Rs ${fmtMoney(monthProfit?.credit_sales)}`} tone="warning" />
                  <MiniStat label="Cash" value={`Rs ${fmtMoney(monthProfit?.cash_payments)}`} tone="success" />
                  <MiniStat label="Card" value={`Rs ${fmtMoney(monthProfit?.card_payments)}`} tone="info" />
                  <MiniStat label="Online" value={`Rs ${fmtMoney(monthProfit?.online_sales)}`} tone="warning" />
                  <MiniStat label="Expenses" value={`Rs ${fmtMoney(monthProfit?.operating_expenses)}`} tone="warning" />
                  <MiniStat label="Purchases" value={`Rs ${fmtMoney(monthProfit?.purchase_expenses)}`} tone="error" />
                  <MiniStat label="Advance Payments" value={`Rs ${fmtMoney(monthProfit?.advance_payments)}`} tone="error" />
                  <MiniStat label="Org Fund" value={`Rs ${fmtMoney(monthProfit?.organization_fund_total)}`} tone="warning" />
                </Box>
              </Stack>

              <Box sx={{ mt: 2.2, pt: 2.2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Card Payment Types
                    </Typography>
                    <BreakdownList items={salesSummary?.card_breakdown} tone="info" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Other Income Details
                    </Typography>
                    <BreakdownList
                      items={monthProfit?.other_income_breakdown}
                      emptyLabel="No other income details"
                      tone="success"
                    />
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <TableSection
              eyebrow="Balance Sheet"
              title="Monthly Profit Position"
              description="Commerce-style statement showing the source side and the charge side for the selected month."
            >
              {!monthProfit ? (
                <EmptyState
                  title="No monthly profit found"
                  description="The selected month does not have enough data to build the profit report."
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Source Side</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                        <TableCell sx={headerCellSx}>Charge Side</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balanceSheetRows.map(([left, right]) => (
                        <TableRow key={`${left.label}-${right.label}`} hover>
                          <TableCell sx={{ fontWeight: left.strong ? 800 : 700, color: left.muted ? 'text.secondary' : 'inherit' }}>
                            {left.label}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: left.strong ? 800 : 400,
                              color: left.success ? 'success.dark' : (left.muted ? 'text.secondary' : 'inherit'),
                            }}
                          >
                            Rs {fmtMoney(left.value)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: right.strong ? 800 : 700 }}>
                            {right.label}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: right.strong ? 800 : 400, color: right.success ? 'success.dark' : 'inherit' }}
                          >
                            Rs {fmtMoney(right.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TableSection>

            <TableSection
              eyebrow="Income Details"
              title="Other Income Breakdown"
              description="Other income means billing-book entries saved with type `income`. Credit settlements and credit returns are added here only when the money is actually received."
            >
              {Array.isArray(monthProfit?.other_income_breakdown) && monthProfit.other_income_breakdown.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Income Head</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthProfit.other_income_breakdown.map((item) => (
                        <TableRow key={item.type}>
                          <TableCell sx={{ fontWeight: 700 }}>{item.type}</TableCell>
                          <TableCell align="right">Rs {fmtMoney(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Total Other Income</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                          Rs {fmtMoney(monthProfit?.other_income)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState
                  title="No other income details"
                  description="There are no billing-book income entries for the selected month."
                />
              )}
            </TableSection>

            <TableSection
              eyebrow="Payroll Composition"
              title="Salary Analysis"
              description="Salary structure for the month, including tips share, 50% service-charge share, bonuses, penalties, and salary cash outflow."
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Metric</TableCell>
                      <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Basic Earning', salaryBreakdownSummary?.basic_earning],
                      ['Tips Share To Salary', salaryBreakdownSummary?.tips_share],
                      ['Service Share To Salary (50%)', salaryBreakdownSummary?.service_share],
                      ['Bonus Total', salaryBreakdownSummary?.bonus_total],
                      ['Gross Salary', salaryBreakdownSummary?.gross_salary],
                      ['Penalty Total', salaryBreakdownSummary?.penalty_total],
                      ['Punishment Half To Organization Fund', salaryBreakdownSummary?.punishment_half_day_fund],
                      ['Loan Deduction', salaryBreakdownSummary?.loan_deduction],
                      ['Net Salary', salaryBreakdownSummary?.net_salary],
                      ['Advance Payments', salaryBreakdownSummary?.advance_deductions],
                      ['Salary Cash Outflow', salaryBreakdownSummary?.salary_cash_outflow],
                    ].map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
                        <TableCell align="right">Rs {fmtMoney(value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSection>

            <TableSection
              eyebrow="Tips And Service"
              title="Allocation Analysis"
              description="Tips are for salary share. Service charge is analyzed as salary share plus organization-fund share."
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Metric</TableCell>
                      <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Tips Collected', tipServiceSummary?.total_tips_collected],
                      ['Tips To Salary', tipServiceSummary?.tips_to_salary],
                      ['Tips To Organization Fund', tipServiceSummary?.tips_to_org_fund],
                      ['Service Charges Collected', tipServiceSummary?.total_service_charges],
                      ['Service To Salary', tipServiceSummary?.service_to_salary],
                      ['Service Removed From Salary', tipServiceSummary?.service_withheld_to_org_fund],
                      ['Service 50% To Organization Fund', tipServiceSummary?.service_org_50],
                      ['Total Service To Organization Fund', tipServiceSummary?.service_total_to_org_fund],
                    ].map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
                        <TableCell align="right">Rs {fmtMoney(value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSection>

            <TableSection
              eyebrow="Organization Fund"
              title="Fund Movements"
              description="Penalties, punishment half-day balances, and removed salary shares move into the organization fund, together with the fixed 50% service-charge share."
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Source</TableCell>
                      <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Penalty Deduction', organizationFundSummary?.penalty_deduction],
                      ['Punishment Half-Day Balance', organizationFundSummary?.punishment_half_day],
                      ['Tips Removed From Salary', organizationFundSummary?.tips_withheld],
                      ['Service Removed From Salary', organizationFundSummary?.service_withheld],
                      ['Service Charge Share (50%)', organizationFundSummary?.service_charge_org_50],
                      ['Organization Fund Total', organizationFundSummary?.total],
                    ].map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: label === 'Organization Fund Total' ? 800 : 700 }}>
                          {label}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: label === 'Organization Fund Total' ? 800 : 400 }}
                        >
                          Rs {fmtMoney(value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSection>
          </>
        )}
      </Box>
    </Box>
  );
}
